import { useGetMulticityResultsQuery, useGetSrpResultsQuery } from '@/src/api/rtkQuery/srpFlightResults'
import { useRtLeg } from '@/src/contexts/RtLegContext'
import { MulticitySearchBody } from '@/src/types/searchQuery'
import {
    selectFlightsAfterFilterCount,
    selectOnwardFilteredCards,
    selectOnwardRecommendedFilter,
    selectOnwardSort,
    selectReturnFilteredCards,
    selectReturnRecommendedFilter,
    selectReturnSort,
    selectShowEmptyFlightScreen
} from '@/src/redux/selectors/srpSelectors'
import { getRecommendedFilter, getSelectedSort } from '@/src/redux/slices/srpStatesSlice'
import { useAppSelector } from '@/src/redux/store'
import { SHOW_SMART_METRICS } from '@/src/utils/srp/aiRecommendation/metrics'
import type { ActivePriceField, RtLegIndex } from '@/src/types/srpJourneyIndex'
import { useMemo } from 'react'
import useGetSearchQueryURL from '../getSearchQueryURL'

/**
 * This hook is used to get the search results from the API with RTK Query.
 * multiple instances of this hook always return updated sync data as it's all states from redux.
 * - call this hooks to get the applied filters and sorted cards.
 *- NOTE: Don't create local states in this hook.
 *
 * @param rtLegOverride - Explicit leg for OW/RT non-solution-view. When omitted,
 *   the leg comes from the ambient RtLegContext (set per column in split view),
 *   defaulting to 0 outside any provider. Multicity is always leg 0 regardless.
 */
const useSearchResults = (rtLegOverride?: RtLegIndex) => {
    const {
        query,
        error: invalidUrl,
        srpViewMode,
        isRT,
        isSolutionView,
        isRTSplitView,
        isPerPaxPricingRequired,
        isMulticity,
        multicityBody,
        journeyCount
    } = useGetSearchQueryURL()

    const legFromContext = useRtLeg()

    const srpResults = useGetSrpResultsQuery(
        // Keep the arg as ONLY { query }: it is the RTK-Query cache key, and every cache patcher
        // (useUpdateSearchResultCache, refetch, getGetSortedTopCards) addresses { query }. Adding a
        // field here would fork the cache entry and silently drop those writes (leg switch / return
        // reprice). The split-view price field is derived from the flag inside transformResponse.
        { query },
        {
            skip: !query || !!invalidUrl || !!isMulticity
        }
    )

    const multicityResults = useGetMulticityResultsQuery(multicityBody as MulticitySearchBody, {
        skip: !isMulticity || !multicityBody || !!invalidUrl
    })

    const {
        data,
        isLoading,
        isFetching,
        error: srpResultsError,
        refetch,
        isUninitialized
    } = isMulticity ? multicityResults : srpResults

    // Engine flag for the price SORT ORDER + grouping mechanism only: the price sort uses the
    // combined RT price (multiAirlinePrice) for any RT search — this "how sort/grouping works" is
    // the same in both RT views and is a separate concern from which price VALUE we show/compare
    // (that's `activePriceField` below). NOT used for the displayed price or the price-range filter.
    // (Equal to the old `isRT && (isSolutionView || isRTSplitView)`: for RT exactly one view boolean
    // is true, so that OR is a tautology and it collapses to `isRT`.)
    const shouldShowMultiAirlinePrices = !!isRT
    const srpResultsLoading = isLoading || isFetching

    const selectedSort = useAppSelector(getSelectedSort)
    const recommendedFilter = useAppSelector(getRecommendedFilter)

    const { cards, sortData, filterData, jsons, sectors, sid, solutionFlowSelectedLeg = 0, smartRecommendationData } = data || {}
    const isPerPaxPricingEnabled = !!isPerPaxPricingRequired && !!jsons?.isPerPaxPricingEnabled

    // Single source of truth for which per-card price VALUE the active view uses across EVERY
    // price surface — card price, sort-tab label, stops/airline filter labels, AND the price-range
    // filter (bounds + inclusion). One field per view, never mixed: split → each leg's individual
    // fare (splitLegFare); RT solution → the combined price; OW → per-pax or retail. (The price
    // sort ORDER is separate — it stays on multiAirlinePrice via sortData; see shouldShowMultiAirlinePrices.)
    const activePriceField: ActivePriceField =
        isRTSplitView
            ? 'splitLegFare'
            : shouldShowMultiAirlinePrices
              ? 'multiAirlinePrice'
              : isPerPaxPricingEnabled
                ? 'totalAdultFare'
                : 'pr'

    // Split view renders both legs at once, so the leg can't be a single global
    // value — it comes from the column's RtLegProvider (via context). An explicit
    // rtLegOverride still wins. Solution view keeps using the cache's
    // solutionFlowSelectedLeg; multicity is always leg 0.
    const effectiveLeg = rtLegOverride ?? legFromContext
    const activeRtLeg: RtLegIndex = isMulticity
        ? 0
        : isSolutionView
          ? solutionFlowSelectedLeg
          : effectiveLeg

    // Use pre-created selectors based on active RT leg - these are shared across all hook instances
    const selectedSortForSelectedJourneyLeg = useAppSelector(
        activeRtLeg === 0 ? selectOnwardSort : selectReturnSort
    )

    const selectedJourneyLegRecommendedFilter = useAppSelector(
        activeRtLeg === 0 ? selectOnwardRecommendedFilter : selectReturnRecommendedFilter
    )


    const selectedLegSortedFilteredData = useAppSelector(state =>
        activeRtLeg === 0
            ? selectOnwardFilteredCards(
                state,
                data,
                shouldShowMultiAirlinePrices,
                srpResultsLoading,
                isPerPaxPricingEnabled,
                activePriceField
            )
            : selectReturnFilteredCards(
                state,
                data,
                shouldShowMultiAirlinePrices,
                srpResultsLoading,
                isPerPaxPricingEnabled,
                activePriceField
            )
    )

    const selectedLegSortedFilteredCards = selectedLegSortedFilteredData?.filteredCards || []
    const smartSortIdMetrics = selectedLegSortedFilteredData.smartSortIdMetrics
    const selectedLegGroupedCards = selectedLegSortedFilteredData?.groupedCards || []
    const smartPreferenceGroupedCards = selectedLegSortedFilteredData?.smartPreferenceGroupedCards || null

    const selectedJourneyLegCheckedBaggageIncludedRecommendedFilter =
        selectedJourneyLegRecommendedFilter?.checkedBaggageIncluded || false

    // Memoized calculations for flight counts
    const totalFlights = useMemo(
        () => (activeRtLeg === 0 ? cards?.[0]?.length : cards?.[1]?.length),
        [activeRtLeg, cards]
    )

    const numberOfFlightsAfterFilter = useMemo(
        () => selectFlightsAfterFilterCount(selectedLegSortedFilteredCards),
        [selectedLegSortedFilteredCards]
    )

    const showEmptyFlightScreen = useMemo(
        () => selectShowEmptyFlightScreen(selectedLegSortedFilteredCards),
        [selectedLegSortedFilteredCards]
    )




    return {
        srpResultsLoading,
        srpResultsError: srpResultsError as {
            data: Record<string, any>
            status: number
        },
        invalidUrl,
        /** Single source of truth for the active view; isSolutionView/isRTSplitView are derived from it. */
        srpViewMode,
        isSolutionView,
        isRTSplitView: !!isRTSplitView,
        isRT,
        isMulticity: !!isMulticity,
        journeyCount: journeyCount ?? 1,
        filterData,
        sortData,
        selectedSortForSelectedJourneyLeg,
        selectedLegSortedFilteredCards,
        selectedLegGroupedCards,
        smartPreferenceGroupedCards,
        jsons,
        sectors,
        /** Active RT leg (0=onward, 1=return). Multicity: always 0. */
        activeRtLeg,
        /** @deprecated Use activeRtLeg — same value, kept for existing OW/RT call sites */
        selectedJourneyLeg: activeRtLeg,
        sid,
        totalFlights,
        numberOfFlightsAfterFilter,
        showEmptyFlightScreen,
        cards,
        selectedSort,
        shouldShowMultiAirlinePrices,
        /** Single price field the view shows/compares everywhere (labels, cards, price-range filter). Never mixed. */
        activePriceField,
        refetch,
        isUninitialized,
        query,
        isPerPaxPricingEnabled,
        smartSortIdMetrics: SHOW_SMART_METRICS ? smartSortIdMetrics : undefined,
        smartRecommendationData: smartRecommendationData?.[activeRtLeg] ?? null
    }
}

export default useSearchResults
